<script lang="ts">
  import type { Snippet } from 'svelte'

  interface Props {
    title: string
    subtitle?: string
    actions?: Snippet
  }

  let { title, subtitle, actions }: Props = $props()
</script>

<header class="page-header">
  <div class="page-header__text">
    <h2>{title}</h2>
    {#if subtitle}<p class="page-header__subtitle">{subtitle}</p>{/if}
  </div>
  {#if actions}
    <div class="page-header__actions">{@render actions()}</div>
  {/if}
</header>

<style lang="scss">
  @use '../assets/styles/variables' as v;

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: v.$space-md;
    // nowrap so a long title shrinks/wraps inside its own column instead of
    // pushing the actions onto a new row — the actions stay pinned top-right
    // regardless of title length. On small screens we re-enable wrapping so the
    // buttons can stack below the title rather than overflow.
    flex-wrap: nowrap;
    padding-bottom: v.$space-md;
    border-bottom: 1px solid v.$color-border;
    margin-bottom: v.$space-md;

    @media (max-width: v.$bp-sm) {
      flex-wrap: wrap;
    }
  }

  .page-header__text {
    // Claim the row and allow shrinking below content width so the title wraps
    // in place (min-width: 0) rather than forcing the header to overflow.
    flex: 1 1 auto;
    min-width: 0;

    h2 {
      margin: 0 0 v.$space-xs;
      font-size: 1.5rem;
      letter-spacing: -0.015em;
    }
  }

  .page-header__subtitle {
    margin: 0;
    color: v.$color-text-muted;
    font-size: 0.9375rem;
  }

  .page-header__actions {
    display: flex;
    gap: v.$space-sm;
    flex-wrap: wrap;
    flex-shrink: 0;

    // On small screens the actions wrap onto their own row; span the full width
    // there so wide buttons (e.g. the detail view's 11rem actions) stack inside
    // the viewport instead of overflowing it horizontally.
    @media (max-width: v.$bp-sm) {
      width: 100%;
    }
  }
</style>
